// src/clock/location.js — location permission + capture (v87.0, inline onclicks v88.1)
//
// Extracted from index.html in v87.0. Two physically-separated regions
// in v86.16 (6090–6258 for the permission UX, 6448–6498 for captureLocation)
// consolidated here. They share _locPermState and call into each other —
// having them in one module removes the action-at-a-distance.
//
// What's here:
//   - _locPermState           → 'granted' | 'denied' | 'prompt' | 'unavailable'
//   - checkLocationPermission → silent permission probe (no native prompt)
//   - renderLocationBanner    → injects the "enable location" banner
//   - dismissLocPrompt        → session-scoped "don't show again"
//   - requestLocationPermission → triggers the native browser prompt
//   - retryLocationPermission → "Try Again" from the help modal
//   - getLocationInstructions → browser-specific instructions HTML
//   - openLocHelp / closeLocHelp → location help modal lifecycle
//   - captureLocation         → called from doClock; geolocate + reverse-geocode
//
// Free-variable dependencies:
//   currentScreen, showToast (index.html), document, navigator, fetch (browser),
//   sessionStorage (browser). All resolve from window at call time.

import '../shared/key-activate.js';   // v136.8: Enter/Space on the role="button" banners below

// ── LOCATION PERMISSION UX ─────────────────────────────────────────
// Silently checks permission state on login and surfaces a banner on the
// clock screen if location services aren't enabled. Won't trigger the
// browser's native prompt unless user explicitly taps the banner.
var _locPermState = 'unknown'; // 'granted' | 'denied' | 'prompt' | 'unavailable'

export async function checkLocationPermission() {
  if (!navigator.geolocation) {
    _locPermState = 'unavailable';
  } else if (navigator.permissions && navigator.permissions.query) {
    try {
      var result = await navigator.permissions.query({ name: 'geolocation' });
      _locPermState = result.state; // 'granted' | 'denied' | 'prompt'
      // React if permission changes while app is open (rare but nice-to-have)
      result.onchange = function(){ _locPermState = result.state; renderLocationBanner(); };
    } catch(e) {
      _locPermState = 'prompt'; // Safari < 16 on iOS throws — assume we need to ask
    }
  } else {
    // Older browsers — no permissions API. Leave as prompt so user can still enable.
    _locPermState = 'prompt';
  }
  renderLocationBanner();
}

export function renderLocationBanner() {
  var holder = document.getElementById('locBannerHolder');
  if (!holder) return;
  // Only render on clock screen — check currentScreen
  if (currentScreen !== 'clock') { holder.innerHTML = ''; return; }
  // Respect "don't show again this session" for the prompt state
  if (_locPermState === 'prompt' && sessionStorage.getItem('locPromptDismissed') === '1') {
    holder.innerHTML = ''; return;
  }

  var html = '';
  // v88.1: inline onclick handlers below use Clock.X form, not bare names.
  // After v87.0 bundled this module into dist/clock.js (IIFE globalName=Clock),
  // bare names like openLocHelp are no longer on window — only Clock.openLocHelp
  // resolves at click time. Same pattern as v88.0 stuck.js inline onclicks.
  if (_locPermState === 'denied') {
    html = '<div class="loc-banner banner-denied" role="button" tabindex="0" onclick="Clock.openLocHelp()">'+
      '<span class="loc-banner-icon">🚫</span>'+
      '<div class="loc-banner-body">'+
        '<div class="loc-banner-title">Location services are disabled</div>'+
        '<div class="loc-banner-sub">Clock-ins will be flagged. Tap to see how to fix.</div>'+
      '</div>'+
    '</div>';
  } else if (_locPermState === 'prompt') {
    html = '<div class="loc-banner banner-prompt" role="button" tabindex="0" onclick="Clock.requestLocationPermission()">'+
      '<span class="loc-banner-icon">📍</span>'+
      '<div class="loc-banner-body">'+
        '<div class="loc-banner-title">Enable location to clock in</div>'+
        '<div class="loc-banner-sub">This app records where you clock in. Tap here to allow it now.</div>'+
      '</div>'+
      '<button class="loc-banner-close" onclick="Clock.dismissLocPrompt(event)" aria-label="Dismiss">×</button>'+
    '</div>';
  } else if (_locPermState === 'unavailable') {
    html = '<div class="loc-banner banner-unavailable">'+
      '<span class="loc-banner-icon">⚠</span>'+
      '<div class="loc-banner-body">'+
        '<div class="loc-banner-title">Location unavailable on this device</div>'+
        '<div class="loc-banner-sub">Clock-ins will be flagged. Try a modern browser.</div>'+
      '</div>'+
    '</div>';
  }
  // 'granted' → empty (silent success)
  holder.innerHTML = html;
}

export function dismissLocPrompt(e) {
  if (e) e.stopPropagation();
  sessionStorage.setItem('locPromptDismissed', '1');
  renderLocationBanner();
}

// User tapped "Enable" banner — trigger native browser prompt
export function requestLocationPermission() {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    function(){
      _locPermState = 'granted';
      renderLocationBanner();
      showToast('📍 Location enabled — you can now clock in', 'success');
    },
    function(err){
      _locPermState = err && err.code === 1 ? 'denied' : 'prompt';
      renderLocationBanner();
      if (_locPermState === 'denied') {
        // They blocked it — show the help modal so they know how to undo
        openLocHelp();
      }
    },
    { timeout: 10000, enableHighAccuracy: false, maximumAge: 0 }
  );
}

// User tapped "Try Again" in the help modal — re-query and close if fixed
export async function retryLocationPermission() {
  await checkLocationPermission();
  if (_locPermState === 'granted') {
    closeLocHelp();
    showToast('📍 Location enabled — you\'re all set', 'success');
  } else if (_locPermState === 'prompt') {
    closeLocHelp();
    requestLocationPermission(); // now trigger the native prompt
  }
  // still 'denied' → leave modal open, user hasn't fixed settings yet
}

// Browser-specific instructions. Detect at runtime, pick the right set.
function getLocationInstructions() {
  var ua = navigator.userAgent || '';
  var isIOS = /iPhone|iPad|iPod/i.test(ua);
  var isAndroid = /Android/i.test(ua);
  var isChrome = /CriOS|Chrome/i.test(ua) && !/Edg\//i.test(ua);
  var isSafari = isIOS && !/CriOS|FxiOS|EdgiOS/i.test(ua);

  if (isIOS && isSafari) {
    return '<h4>iPhone — Safari</h4>'+
      '<ol>'+
        '<li>Open <strong>Settings</strong> (grey gear icon)</li>'+
        '<li>Tap <strong>Privacy &amp; Security → Location Services</strong> → make sure it\'s ON</li>'+
        '<li>Scroll down and tap <strong>Safari Websites</strong> → choose "While Using the App"</li>'+
        '<li>Come back to Safari and tap the <strong>aA</strong> icon in the URL bar</li>'+
        '<li>Choose <strong>Website Settings → Location → Allow</strong></li>'+
        '<li>Reload this page</li>'+
      '</ol>'+
      '<div class="loc-help-note">Tip: if you don\'t see Location in Website Settings, try the other steps first — iOS needs the global toggle on before it shows up.</div>';
  }
  if (isIOS && isChrome) {
    return '<h4>iPhone — Chrome</h4>'+
      '<ol>'+
        '<li>Open <strong>Settings</strong> (grey gear icon)</li>'+
        '<li>Tap <strong>Privacy &amp; Security → Location Services</strong> → make sure it\'s ON</li>'+
        '<li>Scroll through the apps list, find <strong>Chrome</strong>, tap it</li>'+
        '<li>Set <strong>Location</strong> to "While Using the App"</li>'+
        '<li>Force-close Chrome (swipe up from the app switcher) and reopen</li>'+
        '<li>Reload this page</li>'+
      '</ol>'+
      '<div class="loc-help-note">Chrome on iPhone doesn\'t have its own per-site location setting — it\'s all controlled from iPhone Settings.</div>';
  }
  if (isAndroid) {
    return '<h4>Android</h4>'+
      '<ol>'+
        '<li>Tap the <strong>lock icon</strong> in the browser URL bar</li>'+
        '<li>Tap <strong>Permissions</strong> or <strong>Site Settings</strong></li>'+
        '<li>Find <strong>Location</strong> and set it to <strong>Allow</strong></li>'+
        '<li>Also check phone <strong>Settings → Location</strong> is turned on</li>'+
        '<li>Reload this page</li>'+
      '</ol>';
  }
  // Desktop fallback
  return '<h4>Desktop Browser</h4>'+
    '<ol>'+
      '<li>Look for a <strong>location icon</strong> in the address bar (small crosshair, pin, or lock icon on the left)</li>'+
      '<li>Click it and choose <strong>Allow location</strong></li>'+
      '<li>Reload the page</li>'+
    '</ol>'+
    '<div class="loc-help-note">If your organisation has disabled location on this computer (common on work laptops), you may need to ask your IT team to enable it, or use your phone to clock in instead.</div>';
}

export function openLocHelp() {
  document.getElementById('locHelpBody').innerHTML = getLocationInstructions();
  document.getElementById('locHelpModal').classList.add('open');
}
export function closeLocHelp() {
  document.getElementById('locHelpModal').classList.remove('open');
}
(function(){
  var m = document.getElementById('locHelpModal');
  if (m) m.addEventListener('click', function(e){ if (e.target === m) closeLocHelp(); });
})();

// ── GEOTAG LOCATION CAPTURE ───────────────────────────────────────
// Called from doClock on clock_in / clock_out. Returns:
//   { lat, lng, accuracy, label }  on success
//   { denied: true, reason }       on refusal/timeout/unsupported
// Reverse-geocodes via OpenStreetMap Nominatim (free, no API key, rate-limited to ~1/s which is fine for us).
export async function captureLocation() {
  if (!navigator.geolocation) {
    return { denied: true, reason: 'unsupported' };
  }
  // v129.61 (Dubai Hills): a desktop till has no GPS — Windows browsers (Edge
  // AND Chrome, same Wi-Fi/IP lookup) can take >10s on a COLD lookup or return
  // code 2 intermittently, which we then recorded like location being OFF.
  // Two staff on the same shift: the first pays the cold lookup (times out),
  // the second gets the cached fix. So: accept a 10-minute-old fix (a till
  // doesn't move) and RETRY once with a longer window before giving up.
  var getPos = function(timeoutMs) {
    return new Promise(function(resolve, reject) {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: timeoutMs,
        enableHighAccuracy: false, // save battery — indoor GPS is inaccurate anyway
        maximumAge: 600000,        // 10 min cache — shared tills are stationary
      });
    });
  };
  var position;
  try {
    try {
      position = await getPos(10000);
    } catch (first) {
      if (first && first.code === 1) throw first;   // a real denial never retries
      position = await getPos(15000);               // one longer second attempt
    }
  } catch (err) {
    var reason = err && err.code === 1 ? 'denied' : err && err.code === 2 ? 'unavailable' : 'timeout';
    // Keep _locPermState in sync so the banner appears/updates after a denied capture
    if (reason === 'denied') _locPermState = 'denied';
    else if (reason === 'unavailable') _locPermState = 'unavailable';
    if (typeof renderLocationBanner === 'function') renderLocationBanner();
    return { denied: true, reason: reason };
  }
  var lat = position.coords.latitude;
  var lng = position.coords.longitude;
  var accuracy = Math.round(position.coords.accuracy || 0);
  // Try to reverse-geocode — if it fails, we still have the raw coords saved
  var label = null;
  try {
    var res = await fetch(
      'https://nominatim.openstreetmap.org/reverse?format=json&zoom=16&lat=' + lat + '&lon=' + lng,
      { headers: { 'Accept-Language': 'en' } }
    );
    if (res.ok) {
      var data = await res.json();
      if (data && data.display_name) {
        // e.g. "Dubai Mall, Financial Centre Road, Downtown Dubai, Dubai, UAE"
        // → "Dubai Mall, Financial Centre Road"
        var parts = String(data.display_name).split(',').map(function(s){ return s.trim(); });
        label = parts.slice(0, 2).join(', ');
      }
    }
  } catch (e) { /* geocode failed — still have coords */ }
  // Keep _locPermState in sync so the banner auto-dismisses after first successful capture
  _locPermState = 'granted';
  if (typeof renderLocationBanner === 'function') renderLocationBanner();
  return { lat: lat, lng: lng, accuracy: accuracy, label: label };
}
